import { useState, type KeyboardEvent } from 'react';
import { Avatar } from '@/components/Avatar/Avatar';
import { Chip } from '@/components/Chip/Chip';
import { t } from '@/lib/i18n';
import { formatPersonName } from '@/lib/format';
import { useJobRecommendations } from '../hooks';
import { AgentDetailModal, type AgentDetailTarget } from './AgentDetailModal';
import { MemberDropdownPicker } from './MemberDropdownPicker';
import styles from './RecommendedMembersPanel.module.css';

export type AssigneeSelection =
  | { kind: 'member'; id: string }
  | { kind: 'staff_profile'; id: string }
  | null;

interface RecommendedMembersPanelProps {
  templateId: string | null;
  value: AssigneeSelection;
  onChange: (next: AssigneeSelection) => void;
}

/**
 * Three-section picker used on the new-job flow:
 *   1. Hired team members whose Position/Unit matches the template
 *   2. Staff Profiles available to add on submit (atomic add+run)
 *   3. "Other Members" disclosure exposing the flat dropdown for the
 *      free-pick fallback
 *
 * Every recommendation card carries a "More info" button that opens a
 * modal with the full profile (description, personality, system prompt,
 * positions, units). Card body clicks select; nested button clicks don't
 * bubble (stopPropagation) so the two affordances coexist cleanly.
 */
export function RecommendedMembersPanel({
  templateId,
  value,
  onChange,
}: RecommendedMembersPanelProps) {
  const recs = useJobRecommendations(templateId);
  const [otherOpen, setOtherOpen] = useState(false);
  const [detailTarget, setDetailTarget] = useState<AgentDetailTarget | null>(null);

  const added = recs.data?.added ?? [];
  const available = recs.data?.availableToAdd ?? [];
  const hasRecommendations = added.length > 0 || available.length > 0;

  // When no template is chosen yet, or the template carries no position/unit
  // defaults, fall back to the flat dropdown without any disclosure UI.
  if (!templateId || (!recs.isLoading && !hasRecommendations)) {
    return <MemberDropdownPickerSlot value={value} onChange={onChange} />;
  }

  return (
    <div className={styles.panel}>
      {added.length > 0 ? (
        <Section title={t('Recommended — on your team')}>
          <div className={styles.cards}>
            {added.map((m) => (
              <Card
                key={m.id}
                name={formatPersonName(m)}
                positions={m.positions}
                selected={value?.kind === 'member' && value.id === m.id}
                onSelect={() => onChange({ kind: 'member', id: m.id })}
                onMoreInfo={() => setDetailTarget({ kind: 'member', id: m.id })}
                trailing={
                  value?.kind === 'member' && value.id === m.id ? (
                    <span className={styles.check} aria-hidden>
                      ✓
                    </span>
                  ) : null
                }
              />
            ))}
          </div>
        </Section>
      ) : null}

      {available.length > 0 ? (
        <Section title={t('Recommended — available to add')}>
          <p className={styles.sectionHelp}>
            {t('Pick one and they will be added when you Start the job.')}
          </p>
          <div className={styles.cards}>
            {available.map((p) => {
              const selected = value?.kind === 'staff_profile' && value.id === p.id;
              return (
                <Card
                  key={p.id}
                  name={formatPersonName(p)}
                  positions={p.positions}
                  description={p.description}
                  addable
                  selected={selected}
                  onSelect={() => onChange({ kind: 'staff_profile', id: p.id })}
                  onMoreInfo={() => setDetailTarget({ kind: 'staff_profile', id: p.id })}
                  trailing={
                    selected ? (
                      <span className={styles.check} aria-hidden>
                        ✓
                      </span>
                    ) : (
                      <span className={styles.addBadge}>{t('+ Add')}</span>
                    )
                  }
                />
              );
            })}
          </div>
        </Section>
      ) : null}

      <div className={styles.otherWrap}>
        <button
          type="button"
          className={styles.otherToggle}
          onClick={() => setOtherOpen((v) => !v)}
          aria-expanded={otherOpen}
        >
          <span aria-hidden>{otherOpen ? '▾' : '▸'}</span>
          <span>{t('Other team members')}</span>
        </button>
        {otherOpen ? (
          <div className={styles.otherBody}>
            <MemberDropdownPickerSlot value={value} onChange={onChange} />
          </div>
        ) : null}
      </div>

      <AgentDetailModal
        open={detailTarget !== null}
        onOpenChange={(o) => {
          if (!o) setDetailTarget(null);
        }}
        target={detailTarget}
      />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className={styles.section}>
      <h4 className={styles.sectionTitle}>{title}</h4>
      {children}
    </section>
  );
}

interface CardProps {
  name: string;
  positions: { id: string; name: string }[];
  description?: string | null;
  addable?: boolean;
  selected: boolean;
  onSelect: () => void;
  onMoreInfo: () => void;
  trailing: React.ReactNode;
}

/**
 * Pickable recommendation card. Uses div+role="button" rather than a real
 * `<button>` so a nested "More info" button is legal HTML — clicks on the
 * inner button stopPropagation to avoid double-firing.
 */
function Card({
  name,
  positions,
  description,
  addable,
  selected,
  onSelect,
  onMoreInfo,
  trailing,
}: CardProps) {
  function onKey(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect();
    }
  }
  return (
    <div
      role="button"
      tabIndex={0}
      className={[
        styles.card,
        addable ? styles.cardAddable : '',
        selected ? styles.cardSelected : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={onSelect}
      onKeyDown={onKey}
      aria-pressed={selected}
    >
      <Avatar name={name} size="sm" kind="ai" />
      <div className={styles.cardBody}>
        <span className={styles.cardName}>{name}</span>
        {positions.length > 0 ? (
          <div className={styles.chips}>
            {positions.map((p) => (
              <Chip key={p.id} variant="neutral" size="sm">
                {p.name}
              </Chip>
            ))}
          </div>
        ) : description ? (
          <span className={styles.cardSub}>{description}</span>
        ) : null}
      </div>
      <div className={styles.cardActions}>
        <button
          type="button"
          className={styles.moreInfo}
          onClick={(e) => {
            e.stopPropagation();
            onMoreInfo();
          }}
          aria-label={t('More info about {name}', { name })}
        >
          {t('More info')}
        </button>
        {trailing}
      </div>
    </div>
  );
}

/**
 * Adapts the existing flat-list MemberDropdownPicker (which uses a memberId
 * string) into the AssigneeSelection shape consumed by the panel.
 */
function MemberDropdownPickerSlot({
  value,
  onChange,
}: {
  value: AssigneeSelection;
  onChange: (next: AssigneeSelection) => void;
}) {
  const currentMemberId = value?.kind === 'member' ? value.id : null;
  return (
    <MemberDropdownPicker
      value={currentMemberId}
      onChange={(id) => onChange(id ? { kind: 'member', id } : null)}
    />
  );
}
